/**
 * Optiewaarden die een klant nooit mag zien of bestellen, bij geen enkel
 * product, ook al staan ze in de catalogus van de leverancier.
 *
 * - `sample=sample_with_watermark` (Gert, 16 okt 2026): een gratis
 *   proefexemplaar met watermerk over het ontwerp. Nooit tonen, nooit
 *   prijzen, nooit doorzetten. `sample_with_voucher` blijft wel bestaan, met
 *   een korte uitleg (zie `sampleOptionValueDescription`).
 *
 * Deze module wordt op elke laag gebruikt: de optie-API haalt de waarde weg,
 * de configurator verbergt hem, de winkelwagen- en checkoutcontrole keurt hem
 * af, en de gedeelde Print.com-normalisatie (prijs en order) vervangt hem door
 * de neutrale waarde.
 */

const FORBIDDEN_OPTION_VALUES: Record<string, ReadonlySet<string>> = {
  sample: new Set(['sample_with_watermark']),
};

/** De neutrale waarde waarnaar een verboden keuze terugvalt. */
const FORBIDDEN_OPTION_FALLBACK: Record<string, string> = {
  sample: 'none',
};

export function isForbiddenOptionValue(optionId: unknown, value: unknown): boolean {
  const forbidden = FORBIDDEN_OPTION_VALUES[String(optionId ?? '')];
  if (!forbidden || value === undefined || value === null) return false;
  const raw = typeof value === 'object' ? (value as any).value ?? (value as any).id ?? (value as any).slug : value;
  return forbidden.has(String(raw ?? ''));
}

type OptionWithValues = { id?: unknown; name?: unknown; default?: unknown; values?: unknown };

/** Haalt verboden waarden uit een optielijst; een verboden default schuift door. */
export function stripForbiddenOptionValues<T>(options: T): T {
  if (!Array.isArray(options)) return options;
  return options.map((option: OptionWithValues) => {
    const id = String(option?.id ?? option?.name ?? '');
    if (!FORBIDDEN_OPTION_VALUES[id] || !Array.isArray(option.values)) return option;
    const values = (option.values as any[]).filter((value) => !isForbiddenOptionValue(id, value));
    if (values.length === option.values.length) return option;
    const next: OptionWithValues = { ...option, values };
    if (isForbiddenOptionValue(id, option.default)) {
      const fallback = values.find((value) => String(value?.value) === FORBIDDEN_OPTION_FALLBACK[id]) || values[0];
      next.default = fallback?.value;
    }
    return next;
  }) as T;
}

/** Vervangt verboden keuzes in een configuratie door de neutrale waarde. */
export function replaceForbiddenSelections<T extends Record<string, any>>(selection: T): T {
  if (!selection || typeof selection !== 'object') return selection;
  let changed = false;
  const next: Record<string, any> = { ...selection };
  for (const id of Object.keys(FORBIDDEN_OPTION_VALUES)) {
    if (isForbiddenOptionValue(id, next[id])) {
      next[id] = FORBIDDEN_OPTION_FALLBACK[id];
      changed = true;
    }
  }
  return (changed ? next : selection) as T;
}

/** De eerste verboden keuze in een configuratie, of null. */
export function findForbiddenSelection(selection: Record<string, unknown> | null | undefined) {
  if (!selection || typeof selection !== 'object') return null;
  for (const id of Object.keys(FORBIDDEN_OPTION_VALUES)) {
    if (isForbiddenOptionValue(id, selection[id])) return { optionId: id, value: String(selection[id]) };
  }
  return null;
}

/**
 * Klanttekst voor "Sample met voucher". Bij de drukker is dit een betaald
 * proefexemplaar van het eigen ontwerp in de gekozen uitvoering; de excludes
 * staan het alleen toe bij 1 stuk en de prijs is gelijk aan 1 gewoon stuk
 * (sleeves, 16 okt 2026). Bewust zonder leveranciersnaam en zonder belofte
 * over verrekening: de voucher van de drukker komt bij ons binnen, niet bij de
 * klant.
 */
const SAMPLE_WITH_VOUCHER_DESCRIPTION: Record<string, string> = {
  nl: '1 proefexemplaar, volledig bedrukt en afgewerkt met je eigen ontwerp in deze uitvoering. Zo beoordeel je kleur, materiaal en afwerking voordat je de hele oplage bestelt.',
  en: '1 proof copy, fully printed and finished with your own design in this configuration. Check colour, material and finish before you order the full run.',
  de: '1 Musterexemplar, vollständig bedruckt und veredelt mit Ihrem eigenen Design in dieser Ausführung. So prüfen Sie Farbe, Material und Veredelung vor der ganzen Auflage.',
  fr: '1 exemplaire d’essai, entièrement imprimé et façonné avec votre propre design dans cette configuration. Vérifiez couleurs, matière et finition avant de commander tout le tirage.',
};

/** Eén regel op de tegel; de volledige uitleg staat achter het info-icoon.
 * De hele tekst op de tegel maakte hem op mobiel een halve schermhoogte. */
const SAMPLE_WITH_VOUCHER_SHORT: Record<string, string> = {
  nl: '1 proefexemplaar vooraf ter controle',
  en: '1 proof copy to check first',
  de: '1 Musterexemplar zur Prüfung vorab',
  fr: '1 exemplaire d’essai pour vérifier',
};

export function sampleOptionValueShortDescription(optionId: unknown, value: unknown, locale = 'nl'): string | null {
  if (String(optionId ?? '') !== 'sample' || String(value ?? '') !== 'sample_with_voucher') return null;
  const lang = String(locale || 'nl').toLowerCase().split(/[-_]/)[0];
  return SAMPLE_WITH_VOUCHER_SHORT[lang] || SAMPLE_WITH_VOUCHER_SHORT.en;
}

export function sampleOptionValueDescription(optionId: unknown, value: unknown, locale = 'nl'): string | null {
  if (String(optionId ?? '') !== 'sample' || String(value ?? '') !== 'sample_with_voucher') return null;
  const lang = String(locale || 'nl').toLowerCase().split(/[-_]/)[0];
  return SAMPLE_WITH_VOUCHER_DESCRIPTION[lang] || SAMPLE_WITH_VOUCHER_DESCRIPTION.en;
}
